import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonPage,
  IonTitle,
  IonToolbar,
  useIonViewDidLeave,
  useIonViewWillEnter,
} from '@ionic/react'
import React, { BaseSyntheticEvent, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { getMedicalConsultSelector } from '../selectors'
import { chevronBackOutline } from 'ionicons/icons'
import {
  saveMedicalConsult,
  setPhysician,
  setOtherPhysician,
  setMethod,
  setUpdateInputRows,
  setMedicalConsultDefaults,
  setFacility,
} from '../store/medicalConsult.actions'
import { TabConstants } from '../../../../components/tabs/Tabs.constants'
import { getCurrentDate, getCurrentTime, randomNumber } from '../../../../utilities'
import { getMedicalConsultOptionsSelector } from '../../../options/selectors'
import { MedicalConsultConstants } from '../store/medicalConsult.constants'
import { ProcedureConstants } from '../../../../components/procedure/store/procedure.constants'
import { setInputRows, setIsFirstVisit } from '../../../../components/procedure/store/procedure.actions'
import Procedure from '../../../../components/procedure/Procedure.component'
import PCRHeader from '../../../../components/pcrHeader/PCRHeader.component'
import { IOption } from '../../../../common/common.types'
import { showAlert } from '../../../../components/alert/store/alert.actions'
import { compareObjectsIfEqual, filterOutObjectKeys } from '../../../../utilities/object.util'
import { initialMedicalConsultState } from '../store/medicalConsult.reducers'
import { setDeleteProcedure, setProcedureLoaderItem } from '../../../timeline/store/timeline.actions'
import { getDefaultsSelector } from '../../../defaults/selectors/defaults.selector'
import { getPatientDemographicsSelector } from '../../../patientDemographics/selectors'
import { getAgeRangeGroupId, getDefaultsForAgeRange } from '../../../../utilities/procedures.util'
import { getProcedureStateSelector } from '../../../../components/procedure/selectors'
import { goBack } from '../../../../utilities/navigation.util'
import _ from 'lodash'
import { IServiceConfig } from '../../../configurations/store/configurations.types'
import { getServiceConfigSelector } from '../../../configurations/selectors/serviceConfig.selector'
import { IAlertState } from '../../../../components/alert/store/alert.types'
import { TimelineConstants } from '../../../timeline/store/timeline.constants'

const MedicalConsult: React.FC = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const history = useHistory()
  const medicalConsult = useSelector(getMedicalConsultSelector)
  const {
    date,
    time,
    assessedBy,
    successfulAttempt,
    pta,
    physician,
    otherPhysician,
    method,
    facility,
    comments,
    updateInputRows,
    attempts,
    rolePerforming,
    rolePerformingPNNV,
    isEkgImport,
    medicalConsultDefaults,
    procedurePerformedByAnotherUnit,
    procedurePerformedByAnotherUnitPNNV,
  } = medicalConsult
  const medicalConsultOptions = useSelector(getMedicalConsultOptionsSelector)
  const { physicianOptions, methodOptions, facilityOptions } = medicalConsultOptions
  const { defaults } = useSelector(getDefaultsSelector)
  const { dOB, age, ageUnit } = useSelector(getPatientDemographicsSelector)
  const { isFirstVisit } = useSelector(getProcedureStateSelector)
  const serviceConfigData: IServiceConfig = useSelector(getServiceConfigSelector)
  const { DISPLAY_SECONDS } = serviceConfigData
  const displaySeconds = !!DISPLAY_SECONDS

  const save = () => {
    dispatch(saveMedicalConsult())
  }

  const physicianOnChange = (option: IOption) => {
    dispatch(setPhysician(option))
    save()
  }

  const otherPhysicianOnChange = (e: CustomEvent) => {
    e.preventDefault()
    dispatch(setOtherPhysician(e.detail.value))
  }

  const methodOnChange = (option: IOption) => {
    dispatch(setMethod(option))
    save()
  }

  const facilityOnChange = (option: IOption) => {
    dispatch(setFacility(option))
    save()
  }

  const dateTimeValidationCheck = () => {
    if (!date || !time) {
      dispatch(showAlert({ header: '', message: 'Date / Time is required' }))
      return false
    }
    return true
  }

  const initialStateCheck = () => {
    if (pta && ((date && time) || (!date && !time))) return true
    const fieldsToFilter = ['key', 'addToTimeline', 'updateInputRows', 'method', 'medicalConsultDefaults', 'facility']
    const currentState = filterOutObjectKeys(medicalConsult, fieldsToFilter)
    const initalState = filterOutObjectKeys(initialMedicalConsultState, fieldsToFilter)
    if (!compareObjectsIfEqual(currentState, initalState)) {
      return dateTimeValidationCheck()
    }
    return true
  }

  const onSaveClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if (!pta && (!date || !time)) {
      dateTimeValidationCheck()
      return
    }
    const dateTimeValidationCheckResult = initialStateCheck()
    if (dateTimeValidationCheckResult) {
      history.push(TabConstants.TIMELINE_TAB)
    }
  }

  const onBackClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if ((!date || !time) && !pta) {
      const alert: IAlertState = {
        message: t('common.saveTimelineEntry'),
        buttons: [
          {
            text: t('common.buttons.Yes'),
            handler: () => {
              const date = getCurrentDate()
              let time = getCurrentTime()
              if (!displaySeconds) {
                time = time.replace(/(:\d+)(:\d+)$/, '$1:00')
              }

              dispatch({ type: 'medicalConsult/SET_TIME', time: time })
              dispatch({ type: 'medicalConsult/SET_DATE', date: date })
              save()
              goBack()
            },
          },
          {
            text: t('common.buttons.No'),
            role: 'destructive',
            handler: () => {
              const itemToRemove = {
                key: medicalConsult.key,
                rowActionType: TimelineConstants.SET_MEDICAL_CONSULT_PROCEDURE_ITEM,
              }
              dispatch(setDeleteProcedure(true))
              dispatch({ type: TimelineConstants.DELETE_TIMELINE_ITEM, payload: itemToRemove })
              goBack()
            },
          },
        ],
      }
      dispatch(showAlert(alert))
      dispatch(setProcedureLoaderItem(false))
    } else {
      const dateTimeValidationCheck = initialStateCheck()
      if (dateTimeValidationCheck) {
        goBack()
      }
      dispatch(setProcedureLoaderItem(false))
    }
  }

  const inputRows = [
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: MedicalConsultConstants.SET_PHYSICIAN,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: false,
          props: {
            label: t('medicalConsult.physician'),
            dataTestID: 'Physician',
            disabled: !!otherPhysician,
            options: physicianOptions,
            action: physicianOnChange,
            listHeader: 'Physician',
            selectedOption: physician,
          },
        },
        {
          key: MedicalConsultConstants.SET_OTHER_PHYSICIAN,
          type: ProcedureConstants.TEXT,
          hidden: false,
          props: {
            label: t('medicalConsult.otherPhysician'),
            disabled: !!physician,
            dataTestID: 'OtherPhysician',
            inputValue: otherPhysician,
            onChangeAction: otherPhysicianOnChange,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: MedicalConsultConstants.SET_METHOD,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: false,
          props: {
            label: t('medicalConsult.method'),
            dataTestID: 'Method',
            options: methodOptions,
            action: methodOnChange,
            listHeader: 'Method',
            selectedOption: method,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: MedicalConsultConstants.SET_FACILITY,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: facilityOptions?.length < 1,
          props: {
            label: t('medicalConsult.facility'),
            dataTestID: 'Facility',
            options: facilityOptions,
            action: facilityOnChange,
            listHeader: t('medicalConsult.facility'),
            selectedOption: facility,
            hideClearButton: true,
          },
        },
      ],
    },
  ]

  const setMethodDefault = () => {
    const methodDefault = methodOptions.find((option) => option.name === medicalConsultDefaults?.defaultAction)

    if (methodDefault) {
      dispatch(setMethod(methodDefault))
      return
    }

    dispatch(setUpdateInputRows(true))
  }

  const setFacilityDefaultIfPresent = () => {
    if (facilityOptions?.length && _.isEmpty(facility)) {
      dispatch(setFacility(facilityOptions[0]))
    }
  }

  useIonViewWillEnter(() => {
    const ageRange = getAgeRangeGroupId(dOB, age, ageUnit)
    const defaultsForAgeRange = getDefaultsForAgeRange(defaults, 'Medical Consult', ageRange)

    if (defaultsForAgeRange) dispatch(setMedicalConsultDefaults(defaultsForAgeRange))
    else dispatch(setUpdateInputRows(true))
  }, [defaults, dOB, age, ageUnit])

  useEffect(() => {
    if (!isFirstVisit || !medicalConsultDefaults || method) return

    setMethodDefault()
    setFacilityDefaultIfPresent()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medicalConsultDefaults])

  useEffect(() => {
    if (!isFirstVisit || !medicalConsultDefaults || !method) return

    dispatch(setUpdateInputRows(true))
  }, [method, medicalConsultDefaults])

  useIonViewDidLeave(() => {
    dispatch(setIsFirstVisit(false))
  })

  if (updateInputRows) {
    dispatch(setInputRows(inputRows))
    dispatch(setUpdateInputRows(false))
  }

  return (
    <IonPage id='MedicalConsultPage'>
      <PCRHeader />
      <IonHeader>
        <IonToolbar color='light'>
          <IonButtons slot='start'>
            <IonButton data-testid='Back' fill='clear' onClick={onBackClick} color='primary-alt'>
              <IonIcon icon={chevronBackOutline} />
              {t('common.buttons.Back')}
            </IonButton>
          </IonButtons>
          <IonButtons slot='end'>
            <IonButton data-testid='Save' fill='clear' onClick={onSaveClick} color='primary-alt'>
              {t('common.buttons.Save')}
            </IonButton>
          </IonButtons>
          <IonTitle>Medical Consult</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen color='light'>
        <Procedure
          type={'medicalConsult'}
          procedureDate={date}
          procedureTime={time}
          assessedBy={assessedBy}
          successfulAttempt={successfulAttempt}
          comments={comments}
          attempts={attempts}
          rolePerforming={rolePerforming}
          rolePerformingPNNV={rolePerformingPNNV}
          pta={pta}
          isEkgImport={isEkgImport}
          procedurePerformedByAnotherUnit={procedurePerformedByAnotherUnit}
          procedurePerformedByAnotherUnitPNNV={procedurePerformedByAnotherUnitPNNV}
        />
      </IonContent>
    </IonPage>
  )
}

export default MedicalConsult
